import Appbar from 'components/appbar/Appbar';
import { clampMargin, salePriceFromMargin } from 'helpers/margin';
import React, { useCallback, useEffect, useState } from 'react';
import SurgerieNewTabs from './tab/SurgerieNewTabs';
import SurgerieFirstStep from './steps/1/SurgerieFirstStep';
import { NewSurgerieProvider } from './hooks/useNewSurgerie';
import { SurgerieFormData } from 'types/surgerie';
import { Button, styled } from '@mui/material';
import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import SurgerieSecondStep from './steps/2/SurgerieSecondStep';
import SurgerieThirdStep from './steps/3/SurgerieThirdStep';
import SurgerieFourthStep from './steps/4/SurgerieFourthStep';
import { toast } from 'react-toastify';
import SurgerieFifthStep from './steps/5/SurgerieFifthStep';
import { api } from 'services/api';
import { Hospital } from 'types/hospital';
import { ProcedureDoctor, ProcedurePackage } from 'types/procedure';
import SurgerieSixthStep from './steps/6/SurgerieSixthStep';
import { useSelector } from 'store/selector';
import history from 'services/history';
import InsideSaving from 'components/loading/InsideSaving';

const Actions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  flexWrap: 'wrap',
  '& > :last-child': {
    marginLeft: 'auto',
  },
});

const Container = styled('div')(({ theme }) => ({
  padding: '8px 20px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  flex: 1,
  background: '#f7f7f8',
  [theme.breakpoints.down('md')]: {
    padding: '6px 12px 16px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '4px 0 12px',
  },
}));

const Content = styled('div')({
  flex: 1,
});

const SurgerieNew: React.FC = () => {
  const [step, setStep] = useState(1);
  const user = useSelector(state => state.user);
  const [validation, setValidation] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [doctors, setDoctors] = useState<ProcedureDoctor[]>([]);
  const [surgerie, setSurgerie] = useState<SurgerieFormData>({
    patient_id: null,
    patient: null,
    procedure_id: null,
    procedure: null,
    doctor_id: null,
    doctor: null,
    value_doctor: 0,
    hospital_id: null,
    hospital: null,
    value_hospital: 0,
    specialty_id: null,
    specialty: null,
    packages: [],
    value_packages: 0,
    value_anesthetist: 0,
    value_budget: 0,
    value_total: 0,
    value_markup: 0,
    profit_margin: 0,
    created_by: user?.name || '',
  });

  const tabTitle = ['Paciente', 'Procedimento', 'Médico', 'Hospital', 'Produtos', 'Resumo'];

  const buttonText = step === 6 ? 'Finalizar orçamento' : 'Próxima etapa';

  function handleChange<K extends keyof SurgerieFormData>(key: K, value: SurgerieFormData[K]) {
    setValidation('');
    setSurgerie(old => ({ ...old, [key]: value }));
  }

  const validateForm = useCallback((): Promise<string> => {
    return new Promise(resolve => {
      let error = '';

      switch (step) {
        case 1:
          if (!surgerie.patient_id) {
            error = 'Selecione o paciente para continuar';
          }
          break;
        case 2:
          if (!surgerie.procedure_id || !surgerie.specialty_id) {
            error = 'Preencha as informações para continuar';
          }
          break;
        case 3:
          if (!surgerie.doctor_id) {
            error = 'Selecione o médico para continuar';
          }
          break;
        case 4:
          if (!surgerie.hospital_id) {
            error = 'Selecione o hospital para continuar';
          }
          break;
        default:
          break;
      }
      setValidation(error);
      resolve(error);
    });
  }, [step, surgerie]);

  function handleNextStep() {
    validateForm().then(error => {
      if (!error) {
        if (step < 6) {
          setStep(old => old + 1);
        } else {
          handleSubmit();
        }
      } else {
        if (step === 4) {
          toast.error(error);
        }
      }
    });
  }

  function handleSubmit() {
    setSaving(true);

    const { profit_margin: profitMargin, ...budget } = surgerie;

    api
      .post('/budgets', {
        ...budget,
        // o orçamento referencia o médico (doctors.id), não o vínculo médico-procedimento (doctor.id)
        doctor_id: surgerie.doctor?.doctor_id ?? surgerie.doctor_id,
        // a margem efetivamente usada (pode ter sido ajustada pelo valor total) fica registrada no orçamento
        procedure: surgerie.procedure ? { ...surgerie.procedure, profit_margin: profitMargin } : null,
        status: 'open',
      })
      .then(response => {
        toast.success('Orçamento criado com sucesso');
        history.push(`/surgeries/open/${response.data.id_int}/print`);
      })
      .catch(err => {
        const errors = err.response?.data?.errors as Record<string, string[]> | undefined;
        const detail = errors ? Object.values(errors)[0]?.[0] : undefined;

        toast.error(detail || 'Ocorreu um erro ao criar o orçamento');
      })
      .finally(() => {
        setSaving(false);
      });
  }

  function renderStep(stepNumber: number) {
    switch (stepNumber) {
      case 1:
        return <SurgerieFirstStep />;
      case 2:
        return <SurgerieSecondStep />;
      case 3:
        return <SurgerieThirdStep />;
      case 4:
        return <SurgerieFourthStep />;
      case 5:
        return <SurgerieFifthStep />;
      case 6:
        return <SurgerieSixthStep />;
      default:
        return null;
    }
  }

  function handleBackStep() {
    setValidation('');
    if (step > 1) {
      setStep(old => old - 1);
    }
  }

  useEffect(() => {
    if (surgerie.procedure_id) {
      setLoadingHospitals(true);

      api
        .get('/hospitals/all', { params: { active: 1, name: '', procedure_id: surgerie.procedure_id } })
        .then(response => {
          setHospitals(response.data);
        })
        .catch(() => {
          toast.error('Ocorreu um erro ao buscar os hospitais');
        })
        .finally(() => {
          setLoadingHospitals(false);
        });
    }
  }, [surgerie.procedure_id]);

  useEffect(() => {
    if (surgerie.procedure_id) {
      setDoctors(
        surgerie.procedure?.doctors.map(item => ({
          ...item,
          name: item.doctor?.name,
        })) || [],
      );
    }
  }, [surgerie.procedure_id, surgerie.procedure]);

  useEffect(() => {
    setSurgerie(old => ({
      ...old,
      value_packages: surgerie.packages.reduce((acc, item) => acc + (item.package?.sale_price || 0), 0),
    }));
  }, [surgerie.packages]);

  useEffect(() => {
    setSurgerie(old => ({
      ...old,
      value_budget:
        surgerie.value_packages + surgerie.value_doctor + surgerie.value_hospital + surgerie.value_anesthetist,
    }));
  }, [surgerie.value_packages, surgerie.value_doctor, surgerie.value_hospital, surgerie.value_anesthetist]);

  useEffect(() => {
    setSurgerie(old => ({ ...old, profit_margin: clampMargin(old.procedure?.profit_margin) }));
  }, [surgerie.procedure_id]);

  useEffect(() => {
    // o valor de venda sai do custo e da margem de lucro (editável no resumo, junto com o valor total)
    const valueTotal = salePriceFromMargin(surgerie.value_budget, surgerie.profit_margin);

    setSurgerie(old => ({
      ...old,
      value_total: valueTotal,
      value_markup: Math.round((valueTotal - surgerie.value_budget) * 100) / 100,
    }));
  }, [surgerie.value_budget, surgerie.profit_margin]);

  useEffect(() => {
    setSurgerie(old => ({ ...old, value_doctor: surgerie.doctor?.sale_price_max ?? 0 }));
  }, [surgerie.doctor]);

  useEffect(() => {
    setSurgerie(old => ({ ...old, value_hospital: surgerie.hospital?.procedures[0]?.sale_price_max ?? 0 }));
  }, [surgerie.hospital]);

  useEffect(() => {
    if (surgerie.procedure_id) {
      handleChangePackages(
        surgerie.procedure?.packages.map(item => ({
          ...item,
          package: item.package
            ? {
                ...item.package,
                sale_price: item.package?.sale_price_max || 0,
              }
            : null,
        })) || [],
      );
    }
  }, [surgerie.procedure_id, surgerie.procedure]);

  useEffect(() => {
    if (surgerie.procedure) {
      setSurgerie(old => ({
        ...old,
        value_anesthetist: surgerie.procedure?.anesthetist?.sale_price_max || 0,
      }));
    }
  }, [surgerie.procedure]);

  function handleChangePackages(packages: ProcedurePackage[]) {
    setSurgerie(old => ({
      ...old,
      packages,
    }));
  }

  return (
    <NewSurgerieProvider
      value={{
        handleChange,
        surgerie,
        validation,
        doctors,
        hospitals,
        loadingHospitals,
        handleChangePackages,
      }}
    >
      {saving && <InsideSaving />}
      <Appbar title="Novo orçamento cirúrgico" />

      <SurgerieNewTabs setTab={tab => tab <= step && setStep(tab)} tab={step} tabTitle={tabTitle} />

      <Container>
        <Content>{renderStep(step)}</Content>

        <Actions>
          {step > 1 && (
            <Button variant="outlined" color="primary" onClick={handleBackStep} startIcon={<ArrowLeft />}>
              Voltar
            </Button>
          )}

          <Button variant="contained" color="primary" onClick={handleNextStep} endIcon={<ArrowRight />}>
            {buttonText}
          </Button>
        </Actions>
      </Container>
    </NewSurgerieProvider>
  );
};

export default SurgerieNew;
